// src/components/communications/CallLog.jsx
// Call history with recording playback and AI transcription
import React, { useState, useRef } from 'react';
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Play, Pause, Volume2, VolumeX, Download, Clock,
  FileText, Loader, ChevronDown, ChevronUp, Brain,
  AlertCircle, CheckCircle, Tag, ClipboardList, Smile
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function CallLog({ calls, clientId, clientName, clientPhone, onRefresh }) {
  const [expandedCalls, setExpandedCalls] = useState(new Set());
  const [playingCallId, setPlayingCallId] = useState(null);
  const [transcribing, setTranscribing] = useState({});
  const audioRefs = useRef({});

  const toggleExpand = (callId) => {
    const newExpanded = new Set(expandedCalls);
    if (newExpanded.has(callId)) {
      newExpanded.delete(callId);
    } else {
      newExpanded.add(callId);
    }
    setExpandedCalls(newExpanded);
  };

  const handlePlay = (call) => {
    const audioEl = audioRefs.current[call.id];
    if (!audioEl) return;

    if (playingCallId === call.id) {
      audioEl.pause();
      setPlayingCallId(null);
    } else {
      // Pause any other playing audio
      Object.values(audioRefs.current).forEach(el => el?.pause());
      audioEl.play();
      setPlayingCallId(call.id);
    }
  };

  const handleTranscribe = async (call) => {
    const meta = call.metadata || {};
    const recordingSid = meta.recording_sid;

    if (!recordingSid) {
      alert('No recording available for this call');
      return;
    }

    setTranscribing(prev => ({ ...prev, [call.id]: true }));

    try {
      const res = await fetch(`${API_BASE}/calls/transcribe/${recordingSid}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Transcription failed');
      }

      // Refresh to show new transcription
      onRefresh?.();
    } catch (err) {
      alert('Transcription failed: ' + err.message);
    } finally {
      setTranscribing(prev => ({ ...prev, [call.id]: false }));
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    if (diffDays === 0) {
      return `Today at ${time}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${time}`;
    } else if (diffDays < 7) {
      return `${date.toLocaleDateString([], { weekday: 'long' })} at ${time}`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) + ` at ${time}`;
    }
  };

  const getCallIcon = (call) => {
    const meta = call.metadata || {};
    const status = meta.status;
    const isInbound = call.direction === 'inbound' || call.direction === 'in';

    if (status === 'no-answer' || status === 'busy' || status === 'failed') {
      return { icon: PhoneMissed, color: 'text-red-500', bg: 'bg-red-100' };
    }
    if (isInbound) {
      return { icon: PhoneIncoming, color: 'text-blue-500', bg: 'bg-blue-100' };
    }
    return { icon: PhoneOutgoing, color: 'text-green-500', bg: 'bg-green-100' };
  };

  const getCallStatus = (call) => {
    const meta = call.metadata || {};
    const status = meta.status;

    if (status === 'completed') return { text: 'Completed', color: 'text-green-600' };
    if (status === 'no-answer') return { text: 'No Answer', color: 'text-red-600' };
    if (status === 'busy') return { text: 'Busy', color: 'text-orange-600' };
    if (status === 'failed') return { text: 'Failed', color: 'text-red-600' };
    if (status === 'in-progress') return { text: 'In Progress', color: 'text-blue-600' };
    return { text: status || 'Unknown', color: 'text-gray-600' };
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-50';
      case 'negative': return 'text-red-600 bg-red-50';
      case 'concerned': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {calls.length} {calls.length === 1 ? 'call' : 'calls'}
          </span>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Call List */}
      <div className="flex-1 overflow-y-auto">
        {calls.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
            <Phone className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No calls yet</p>
            <p className="text-sm text-center mt-1">
              Call history will appear here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {calls.map((call) => {
              const isExpanded = expandedCalls.has(call.id);
              const meta = call.metadata || {};
              const callInfo = getCallIcon(call);
              const status = getCallStatus(call);
              const hasRecording = !!meta.recording_sid;
              const hasTranscript = !!meta.transcript;
              const analysis = meta.ai_analysis ? (typeof meta.ai_analysis === 'string' ? JSON.parse(meta.ai_analysis) : meta.ai_analysis) : null;
              const isTranscribing = transcribing[call.id];

              return (
                <div key={call.id} className="bg-white">
                  {/* Call Header */}
                  <button
                    onClick={() => toggleExpand(call.id)}
                    className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left"
                  >
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${callInfo.bg}`}>
                      <callInfo.icon className={`w-6 h-6 ${callInfo.color}`} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {call.direction === 'inbound' ? `From ${clientName}` : `To ${clientName}`}
                        </span>
                        {hasRecording && (
                          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                            Recorded
                          </span>
                        )}
                        {hasTranscript && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                            Transcribed
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-sm ${status.color}`}>{status.text}</span>
                        {meta.duration && (
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDuration(parseInt(meta.duration))}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Timestamp & Expand */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm text-gray-500">{formatDate(call.created_at)}</span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4">
                      <div className="ml-16 space-y-4">
                        {/* Recording Player */}
                        {hasRecording && (
                          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center gap-3 mb-3">
                              <Volume2 className="w-5 h-5 text-gray-500" />
                              <span className="font-medium text-gray-700">Call Recording</span>
                            </div>

                            <div className="flex items-center gap-3">
                              {/* Play Button */}
                              <button
                                onClick={() => handlePlay(call)}
                                className="w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all"
                              >
                                {playingCallId === call.id ? (
                                  <Pause className="w-5 h-5" />
                                ) : (
                                  <Play className="w-5 h-5 ml-0.5" />
                                )}
                              </button>

                              {/* Audio Element */}
                              <audio
                                ref={el => audioRefs.current[call.id] = el}
                                src={`${API_BASE}/calls/recording/${meta.recording_sid}`}
                                onEnded={() => setPlayingCallId(null)}
                                className="flex-1"
                                controls
                              />
                            </div>

                            {/* Transcribe Button */}
                            {!hasTranscript && (
                              <button
                                onClick={() => handleTranscribe(call)}
                                disabled={isTranscribing}
                                className="mt-3 flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                              >
                                {isTranscribing ? (
                                  <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    Transcribing...
                                  </>
                                ) : (
                                  <>
                                    <Brain className="w-4 h-4" />
                                    Transcribe with AI
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}

                        {/* AI Analysis */}
                        {analysis && (
                          <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
                            <div className="flex items-center gap-2 mb-3">
                              <Brain className="w-5 h-5 text-purple-600" />
                              <span className="font-semibold text-purple-900">AI Call Analysis</span>
                            </div>

                            {/* Summary */}
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-600 mb-1">Summary</h4>
                              <p className="text-gray-800 leading-relaxed">{analysis.summary}</p>
                            </div>

                            {/* Sentiment */}
                            {analysis.sentiment && (
                              <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-600 mb-1 flex items-center gap-1">
                                  <Smile className="w-4 h-4" />
                                  Client Sentiment
                                </h4>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${getSentimentColor(analysis.sentiment)}`}>
                                  {analysis.sentiment}
                                </span>
                              </div>
                            )}

                            {/* Topics */}
                            {analysis.topics?.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                                  <Tag className="w-4 h-4" />
                                  Topics Discussed
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {analysis.topics.map((topic, idx) => (
                                    <span key={idx} className="px-3 py-1 bg-white text-gray-700 text-sm rounded-full border border-gray-200">
                                      {topic}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Action Items */}
                            {analysis.actionItems?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                                  <ClipboardList className="w-4 h-4" />
                                  Action Items
                                </h4>
                                <ul className="space-y-2">
                                  {analysis.actionItems.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Transcript */}
                        {hasTranscript && (
                          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center gap-2 mb-3">
                              <FileText className="w-5 h-5 text-gray-600" />
                              <span className="font-medium text-gray-700">Full Transcript</span>
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm">
                                {meta.transcript}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* No Recording Message */}
                        {!hasRecording && (
                          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-center text-gray-500">
                            <Phone className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            <p>No recording available for this call</p>
                            <p className="text-sm mt-1">Enable recording in your Twilio settings</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
